import { ref } from 'vue'
import { updatePreset } from '@primeuix/themes'

export type ThemeColor =
  | 'nebula'
  | 'emerald'
  | 'green'
  | 'lime'
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose'

const THEME_STORAGE_KEY = 'primevue-theme-color'

/**
 * Composable para cambiar el color primario del theme de PrimeVue v4
 * usando la Theming API en "styled mode"
 */
export function useThemeSwitcher() {
  // Cargar el tema guardado o usar 'nebula' por defecto
  const currentColor = ref<ThemeColor>(
    (localStorage.getItem(THEME_STORAGE_KEY) as ThemeColor) || 'nebula'
  )

  /**
   * Cambia el color primario del theme dinámicamente
   * Mapea el color seleccionado a los tokens semantic.primary
   */
  const changeThemeColor = (color: ThemeColor) => {
    updatePreset({
      semantic: {
        primary: {
          50: `{${color}.50}`,
          100: `{${color}.100}`,
          200: `{${color}.200}`,
          300: `{${color}.300}`,
          400: `{${color}.400}`,
          500: `{${color}.500}`,
          600: `{${color}.600}`,
          700: `{${color}.700}`,
          800: `{${color}.800}`,
          900: `{${color}.900}`,
          950: `{${color}.950}`,
        },
      },
    })

    currentColor.value = color
    localStorage.setItem(THEME_STORAGE_KEY, color)
  }

  /**
   * Inicializa el theme con el color guardado
   * Debe llamarse al montar el componente principal
   */
  const initTheme = () => {
    const savedColor = localStorage.getItem(THEME_STORAGE_KEY) as ThemeColor
    if (savedColor) {
      changeThemeColor(savedColor)
    }
  }

  /**
   * Lista de colores disponibles para el selector
   */
  const availableColors: { label: string; value: ThemeColor; hex: string }[] = [
    { label: 'Nebula', value: 'nebula', hex: '#9C97C9' },
    { label: 'Emerald', value: 'emerald', hex: '#10b981' },
    { label: 'Green', value: 'green', hex: '#22c55e' },
    { label: 'Lime', value: 'lime', hex: '#84cc16' },
    { label: 'Red', value: 'red', hex: '#ef4444' },
    { label: 'Orange', value: 'orange', hex: '#f97316' },
    { label: 'Amber', value: 'amber', hex: '#f59e0b' },
    { label: 'Yellow', value: 'yellow', hex: '#eab308' },
    { label: 'Teal', value: 'teal', hex: '#14b8a6' },
    { label: 'Cyan', value: 'cyan', hex: '#06b6d4' },
    { label: 'Sky', value: 'sky', hex: '#0ea5e9' },
    { label: 'Blue', value: 'blue', hex: '#3b82f6' },
    { label: 'Indigo', value: 'indigo', hex: '#6366f1' },
    { label: 'Violet', value: 'violet', hex: '#8b5cf6' },
    { label: 'Purple', value: 'purple', hex: '#a855f7' },
    { label: 'Fuchsia', value: 'fuchsia', hex: '#d946ef' },
    { label: 'Pink', value: 'pink', hex: '#ec4899' },
    { label: 'Rose', value: 'rose', hex: '#f43f5e' },
  ]

  return {
    currentColor,
    availableColors,
    changeThemeColor,
    initTheme,
  }
}
